/**
 * timeFrameHelpers.ts
 * Converts QuickSelect time frame pills to date ranges.
 * Requires currentFiscalContext from period-weeks API.
 */

import type { CurrentFiscalContext } from '../../../../../api/periodWeeksApi';

// ─── Date Utility Functions ─────────────────────────────────────────────

/**
 * Converts a Date object to ISO format string (YYYY-MM-DD).
 */
function dateToIso(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Adds one day to an ISO date string (YYYY-MM-DD).
 * Backend date-range filters treat endDate as an EXCLUSIVE upper bound (confirmed on
 * both /ontime-dispatch/load-execution and /ontime-dispatch/executive-overview: a
 * same-day startDate=endDate returns zero rows), so an inclusive end date must be
 * advanced by one day before being sent as endDate.
 */
export function addOneDayToIsoDate(dateStr: string): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day + 1));
  return date.toISOString().slice(0, 10);
}

/**
 * Gets the Monday of the week containing the given date.
 * Used for "Week-to-Date" calculation.
 */
function getMonday(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.setDate(diff));
}

/**
 * Gets the start date of a fiscal period.
 * Assumes 4-week periods starting from Jan 1.
 * 
 * @param year - Fiscal year
 * @param period - Period number (1-13)
 * @returns Date object for the first day of the period
 */
function getPeriodStartDate(year: number, period: number): Date {
  const startDayOfYear = (period - 1) * 28 + 1;
  const d = new Date(year, 0, 1);
  d.setDate(d.getDate() + startDayOfYear - 1);
  return d;
}

/**
 * Gets the end date of a fiscal period.
 * Assumes 4-week periods.
 * 
 * @param year - Fiscal year
 * @param period - Period number (1-13)
 * @returns Date object for the last day of the period
 */
function getPeriodEndDate(year: number, period: number): Date {
  const d = getPeriodStartDate(year, period);
  d.setDate(d.getDate() + 27); // 28 days - 1
  return d;
}

/**
 * Gets the first quarter start period number.
 * Given a period number, returns the period where the quarter starts.
 * E.g., period 8 → Q3 starts at period 7 → returns 7
 */
function getQuarterStartPeriod(period: number): number {
  return Math.floor((period - 1) / 3) * 3 + 1;
}

// ─── Main Export ────────────────────────────────────────────────────────

/**
 * Converts a QuickSelect time frame to a date range.
 * 
 * Supported time frames:
 * - 'wtd': Week-to-Date (Monday to today)
 * - 'ptd': Period-to-Date (Start of current period to today)
 * - 'qtd': Quarter-to-Date (Start of current quarter to today)
 * - 'ytd': Year-to-Date (Jan 1 to today)
 * - 'lcw': Last Closed Week (Full previous week)
 * - 'lcp': Last Closed Period (Full previous period)
 * 
 * @param quickSelect - The selected time frame
 * @param currentFiscalContext - Current fiscal context from API (required)
 * @returns Object with start and end dates in ISO format
 * @throws Error if currentFiscalContext is missing or quickSelect is invalid
 */
export function getDateRangeFromQuickSelect(
  quickSelect: 'wtd' | 'ptd' | 'qtd' | 'ytd' | 'lcw' | 'lcp',
  currentFiscalContext?: CurrentFiscalContext
): { start: string; end: string } {
  if (!currentFiscalContext) {
    throw new Error('currentFiscalContext is required to convert QuickSelect to date range');
  }

  const today = new Date(currentFiscalContext.date);
  const year = currentFiscalContext.year;
  const period = currentFiscalContext.period;

  let start: Date;
  let end: Date;

  switch (quickSelect) {
    case 'wtd': {
      // Week to Date: Monday of current week to today
      start = getMonday(today);
      end = today;
      break;
    }

    case 'ptd': {
      // Period to Date: First day of current period to today
      start = getPeriodStartDate(year, period);
      end = today;
      break;
    }

    case 'qtd': {
      // Quarter to Date: First day of current quarter to today
      const quarterStartPeriod = getQuarterStartPeriod(period);
      start = getPeriodStartDate(year, quarterStartPeriod);
      end = today;
      break;
    }

    case 'ytd': {
      // Year to Date: Jan 1 of fiscal year to today
      start = new Date(year, 0, 1);
      end = today;
      break;
    }

    case 'lcw': {
      // Last Closed Week: Full week before current week
      const lastWeekEnd = new Date(today);
      lastWeekEnd.setDate(today.getDate() - today.getDay() - 1);
      const lastWeekStart = new Date(lastWeekEnd);
      lastWeekStart.setDate(lastWeekEnd.getDate() - 6);
      start = lastWeekStart;
      end = lastWeekEnd;
      break;
    }

    case 'lcp': {
      // Last Closed Period: Full period before current period
      const lastPeriod = period === 1 ? 13 : period - 1;
      const lastPeriodYear = period === 1 ? year - 1 : year;
      start = getPeriodStartDate(lastPeriodYear, lastPeriod);
      end = getPeriodEndDate(lastPeriodYear, lastPeriod);
      break;
    }

    default:
      throw new Error(`Unknown quickSelect value: ${quickSelect}`);
  }

  return {
    start: dateToIso(start),
    end: dateToIso(end),
  };
}

/**
 * Checks if a date range is valid (start <= end).
 * Exported for validation purposes.
 */
export function isValidDateRange(start: string, end: string): boolean {
  return start <= end;
}
